import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Sparkles, CheckCircle2, ChevronRight, X, Trophy,
  Camera, FileText, Image as ImageIcon, MapPin, Briefcase,
  Globe, Award, ShieldCheck, Star, TrendingUp, DollarSign,
  Calendar, Phone, Mail, User as UserIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Iter-168 Phase 3 — Talent Profile Quality (Completeness) Card.
 *
 * Drop-in dashboard widget showing weighted score 0–100, Top Profile
 * milestone, and dismissible per-category prompts. Positive
 * gamification — UX rule is "encourage, don't punish".
 */

const CATEGORY_ICONS = {
  profile_photo:      Camera,
  gallery_photos:     ImageIcon,
  bio:                FileText,
  verified_email:     Mail,
  verified_phone:     Phone,
  location:           MapPin,
  gig_roles:          Briefcase,
  experience_history: Briefcase,
  social_links:       Globe,
  certifications:     Award,
  resume_media:       ImageIcon,
  ratings:            Star,
  response_rate:      TrendingUp,
  reliability:        ShieldCheck,
  idv_verified:       ShieldCheck,
  minimum_rate:       DollarSign,
  date_of_birth:      Calendar,
};

// Where each category links the talent for fastest fix.
const CATEGORY_ACTIONS = {
  profile_photo:      { path: '/talent/profile-info', label: 'Add photo' },
  gallery_photos:     { path: '/talent/portfolio',    label: 'Upload photos' },
  bio:                { path: '/talent/profile-info', label: 'Write bio' },
  verified_email:     { path: '/talent/profile',      label: 'Verify email' },
  verified_phone:     { path: '/talent/profile',      label: 'Add phone' },
  location:           { path: '/talent/profile-info', label: 'Set location' },
  gig_roles:          { path: '/talent/gig-roles',    label: 'Pick roles' },
  experience_history: { path: '/talent/profile-info', label: 'Add experience' },
  social_links:       { path: '/talent/profile-info', label: 'Add links' },
  certifications:     { path: '/talent/certifications', label: 'Add cert' },
  resume_media:       { path: '/talent/portfolio',    label: 'Upload media' },
  ratings:            null,  // earned automatically
  response_rate:      null,  // earned automatically
  reliability:        null,  // earned automatically
  idv_verified:       { path: '/talent/profile',      label: 'Verify ID' },
  minimum_rate:       { path: '/talent/profile-info', label: 'Set rate' },
  date_of_birth:      { path: '/talent/profile-info', label: 'Add DOB' },
};

const ProfileQualityCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/profile-quality');
      setData(res.data);
    } catch (e) {
      // Endpoint should always be reachable for talent; if not, fail silent
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = async (key) => {
    try {
      await api.post('/profile-quality/dismiss-prompt', { category_key: key });
      setData(prev => prev ? { ...prev, dismissed_prompts: [...(prev.dismissed_prompts || []), key] } : prev);
    } catch (e) {
      toast.error('Could not dismiss reminder');
    }
  };

  const restoreAll = async () => {
    try {
      await api.post('/profile-quality/restore-prompts', { category_keys: [] });
      load();
    } catch (e) {
      toast.error('Could not reset reminders');
    }
  };

  if (loading || !data) return null;

  const { score, is_top_profile, top_threshold, categories = [], dismissed_prompts = [] } = data;

  const incomplete = categories.filter(c => c.status !== 'complete');
  const visible    = incomplete.filter(c => !dismissed_prompts.includes(c.key));
  const completedCount = categories.length - incomplete.length;

  const tone =
    score >= 85 ? { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' } :
    score >= 60 ? { badge: 'bg-cyan-100 text-cyan-700 border-cyan-200' } :
                  { badge: 'bg-amber-100 text-amber-700 border-amber-200' };

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm"
      data-testid="profile-quality-card"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-500" />
          <h3 className="font-semibold text-gray-900">Profile Quality</h3>
        </div>
        <Badge className={`${tone.badge} font-semibold border`} data-testid="profile-quality-score">
          {score}/100
        </Badge>
      </div>
      {/* Iter-225n — clarify the difference from "Booking readiness".
          Quality is a 17-category weighted score that drives Discovery
          ranking. Readiness is a 4-item compliance check. */}
      <p
        className="text-[11px] text-gray-500 mb-2 leading-snug"
        data-testid="profile-quality-explainer"
      >
        Weighted across 17 profile categories. Different from "Booking readiness" — you can be 100% ready to book and still grow Quality with photos, certs, experience, and ratings.
      </p>

      {is_top_profile ? (
        <div
          className="flex items-center gap-2 text-xs font-semibold text-emerald-700 mb-3"
          data-testid="top-profile-status"
        >
          <Trophy className="w-3.5 h-3.5" />
          You're a Top Profile — clients see your full badge.
        </div>
      ) : (
        <p className="text-xs text-gray-500 mb-3" data-testid="top-profile-progress-hint">
          Reach <strong>{top_threshold}</strong> to earn the Top Profile badge — clients
          rank you higher and book faster.
        </p>
      )}

      <Progress
        value={score}
        className="h-2 mb-1"
        data-testid="profile-quality-progress"
      />
      <div className="flex items-center justify-between text-[11px] text-gray-500 mb-4">
        <span>{completedCount} of {categories.length} done</span>
        {dismissed_prompts.length > 0 && (
          <button
            type="button"
            onClick={restoreAll}
            className="text-cyan-600 hover:underline"
            data-testid="restore-all-prompts-btn"
          >
            Show {dismissed_prompts.length} hidden
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
          <p className="text-sm text-emerald-700">
            {is_top_profile ? "You're crushing it. Great profile!" : "Nice work — every box you've ticked moves you up the rankings."}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="profile-quality-prompts">
          {visible.slice(0, 6).map((cat) => {
            const Icon = CATEGORY_ICONS[cat.key] || UserIcon;
            const action = CATEGORY_ACTIONS[cat.key];
            return (
              <li
                key={cat.key}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition"
                data-testid={`profile-quality-prompt-${cat.key}`}
              >
                <Icon className="w-4 h-4 text-cyan-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{cat.label}</p>
                  <p className="text-xs text-gray-500 truncate">{cat.hint}</p>
                </div>
                <span className="text-[10px] font-semibold text-gray-400 shrink-0">+{cat.weight}</span>
                {action && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-cyan-600 shrink-0 text-xs h-7"
                    onClick={() => navigate(action.path)}
                    data-testid={`profile-quality-action-${cat.key}`}
                  >
                    {action.label} <ChevronRight className="w-3 h-3 ml-0.5" />
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() => dismiss(cat.key)}
                  className="text-gray-300 hover:text-gray-500 shrink-0"
                  aria-label="Hide reminder"
                  data-testid={`profile-quality-dismiss-${cat.key}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProfileQualityCard;
